import { useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";

import { DateTime } from "luxon";
import {
  Typography,
  Box,
  Card,
  Grid2 as Grid,
  Container,
  CardContent,
  CircularProgress,
  useMediaQuery,
  Tabs,
  Tab,
  Fade,
  Chip,
  Badge,
  IconButton,
  Tooltip,
  Alert,
  Snackbar,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import {
  Visibility,
  CheckCircle,
  Schedule,
  Refresh,
  Assessment,
  Timeline,
  History,
} from "@mui/icons-material";

// Import modals
import ScheduledModal from "./modals/ScheduledModal";
import CompletedModal from "./modals/CompletedModal";
import UpcomingNotarizationModal from "./modals/UpcomingNotarizationModal";
import PastNotarizationModal from "./modals/PastNotarizationModal";
import RealTimeRequestModal from "./modals/RealTimeRequestModal";
import ExpiredExpeditedRequestModal from "./modals/ExpiredExpeditedRequestModal";
import { connectWebsocket } from "../../store/actions/websocketActions";
import {
  fetchNotaryData,
  fetchActiveRealTimeRequestsOnce,
  fetchExpiredExpeditedRequestsOnce,
} from "../../store/actions/notaryActions";
import { openInNewTab } from "../../utils/openNewTab";
import {
  expiredRequestColumns,
  pastNotarizationColumns,
  realTimeRequestColumns,
  upcomingNotarizationColumns,
} from "./notaryJobManagementColumns";

const NotaryJobManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const extraSmall = useMediaQuery((theme) => theme.breakpoints.down("xsm"));
  const tabletSize = useMediaQuery((theme) => theme.breakpoints.down("md"));

  const currentUser = useSelector((state) => state.notarySession.user);
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const realTimeRequests = useSelector(
    (state) => state.notarySession.notaryRequests,
  );
  const navigateToRealRequestsTab = useSelector(
    (state) => state.notarySession.navigateToRealRequestsTab,
  );
  const sessions = useSelector((state) => state.sessions);

  // Loading states
  const [loading, setLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Tab management
  const [activeTab, setActiveTab] = useState(0);

  // Get data from Redux state (updated via websocket)
  const upcomingNotarizations = useMemo(
    () => currentUser?.upcoming_notarizations || [],
    [currentUser?.upcoming_notarizations],
  );
  const pastNotarizations = currentUser?.past_notarizations || [];
  const expiredExpeditedRequests =
    currentUser?.expired_expedited_requests || [];

  // Countdown timer state - updates every second to force re-render
  const [currentTime, setCurrentTime] = useState(Date.now());

  const dedupedUpcomingNotarizations = useMemo(() => {
    const seen = new Set();

    return upcomingNotarizations.filter((row) => {
      const signerEmail = (
        row?.signer?.email ||
        row?.signer_details?.email ||
        ""
      )
        .trim()
        .toLowerCase();

      const stableNotarizationId =
        row?.notarization_id ||
        row?.bn_session_id ||
        row?.task_id ||
        row?.cst_packageID ||
        "";

      const normalizedBookedDate = (() => {
        const parsed = DateTime.fromISO(row?.booked_date || "", {
          setZone: true,
        });
        if (!parsed.isValid) {
          return row?.booked_date || "";
        }
        return parsed.toUTC().startOf("minute").toISO();
      })();

      const normalizedAddress = (row?.project_address || "")
        .trim()
        .toLowerCase();

      const key = [
        stableNotarizationId,
        signerEmail,
        normalizedBookedDate,
        normalizedAddress,
      ].join("|");

      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [upcomingNotarizations]);

  const normalizeUtcIsoString = (value) => {
    if (!value || typeof value !== "string") return value;

    const hasTimezone = /(?:Z|[+-]\d{2}:\d{2})$/.test(value);
    return hasTimezone ? value : `${value}Z`;
  };

  const getRequestExpiryTime = (requestRow) => {
    if (requestRow?.expires_at) {
      const expiresAtParsed = DateTime.fromISO(
        normalizeUtcIsoString(requestRow.expires_at),
        {
          setZone: true,
        },
      );
      if (expiresAtParsed.isValid) {
        return expiresAtParsed.toUTC();
      }
    }

    const createdAt = DateTime.fromISO(
      normalizeUtcIsoString(requestRow?.created_at || ""),
      {
        setZone: true,
      },
    );
    if (createdAt.isValid) {
      return createdAt.plus({ minutes: 5 });
    }

    return null;
  };

  // Frontend-only UX rule:
  // keep expired rows visible for 5 seconds, then remove from list.
  const visibleRealTimeRequests = useMemo(() => {
    const now = DateTime.fromMillis(currentTime, { zone: "utc" });

    return (realTimeRequests || []).filter((row) => {
      const expiryTime = getRequestExpiryTime(row);
      if (!expiryTime || !expiryTime.isValid) return true;

      const hideAt = expiryTime.plus({ seconds: 5 });
      return now < hideAt;
    });
  }, [realTimeRequests, currentTime]);

  // Modal states
  const [scheduledModalOpen, setScheduledModalOpen] = useState(false);
  const [completedModalOpen, setCompletedModalOpen] = useState(false);
  const [upcomingModalOpen, setUpcomingModalOpen] = useState(false);
  const [pastModalOpen, setPastModalOpen] = useState(false);
  const [realTimeModalOpen, setRealTimeModalOpen] = useState(false);
  const [expiredExpeditedModalOpen, setExpiredExpeditedModalOpen] =
    useState(false);
  const [expiredAcceptConfirmOpen, setExpiredAcceptConfirmOpen] =
    useState(false);
  const [expiredAcceptConfirmJob, setExpiredAcceptConfirmJob] = useState(null);

  const [selectedJob, setSelectedJob] = useState(null);
  const [selectedButton, setSelectedButton] = useState("reminder");

  // Error handling
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [cancelErrorText, setCancelErrorText] = useState("");
  const [rescheduleErrorText, setRescheduleErrorText] = useState("");
  const [cancelLoading, setCancelLoading] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);

  const mobileSize = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  // Update currentTime every second to make countdown timers live
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Ensure notary websocket is connected (safety net in case app-level missed)
  useEffect(() => {
    const ws = sessions?.notarySession;
    if (ws?.isValidated && ws?.token && !ws?.wsConnected) {
      const timer = setTimeout(() => {
        dispatch(connectWebsocket("notarySession", ws.token));
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [
    sessions?.notarySession?.wsConnected,
    sessions?.notarySession?.token,
    sessions?.notarySession?.isValidated,
    sessions?.notarySession,
    dispatch,
  ]);

  // Shared DataGrid layout to match MyPermitGrid
  const commonDataGridSx = {
    "&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
      outline: "none !important",
    },
    "& .MuiDataGrid-columnHeader:focus-within": {
      outline: "none !important",
    },
    "& .MuiDataGrid-columnHeader": {
      "&:hover": {
        ".MuiDataGrid-menuIcon, .MuiDataGrid-sortIcon, .MuiDataGrid-columnHeader--sorted":
          {
            visibility: "hidden",
            pointerEvents: "none",
          },
        cursor: "default",
      },
      paddingLeft: 1,
      paddingRight: 1,
      margin: 0,
      backgroundColor: "#333",
      color: "#fff",
      borderBottom: "1px solid #ccc",
    },
    "& .MuiDataGrid-columnHeader--sortable": {
      pointerEvents: "none",
    },
    "& .MuiDataGrid-columnHeaders": {
      padding: 0,
      margin: 0,
    },
    "& .MuiDataGrid-columnHeadersInner": {
      width: "100%",
      padding: 0,
    },
    border: "1px solid #FCF2F0",
    "& .MuiDataGrid-cell": {
      borderColor: "#333333",
    },
    "& .MuiDataGrid-columnSeparator": {
      display: "none",
    },
    ".MuiDataGrid-cell:focus-within": {
      outline: "none !important",
    },
    "& .MuiDataGrid-row": {
      cursor: "pointer",
    },
  };

  // No need for separate API call

  // Data is loaded via websocket from get_notary_update_object
  // Only set loading state based on whether currentUser exists
  useEffect(() => {
    if (currentUser?.availabilities.length === 0) {
      navigate("/notary/finalize");
      return;
    }
    // Data is already loaded from initial notary session fetch via websocket
    if (currentUser) {
      setLoading(false);
      setHasLoadedOnce(true);
    }
  }, [currentUser, navigate]);

  // Listen for Redux action to navigate to real requests tab
  useEffect(() => {
    if (navigateToRealRequestsTab) {
      setActiveTab(2); // Switch to "Real-time Requests" tab
      dispatch({ type: "CLEAR_NAVIGATE_TO_REAL_REQUESTS_TAB" }); // Clear the flag
    }
  }, [navigateToRealRequestsTab, dispatch]);

  // Handle navigation state for auto-selecting tabs
  useEffect(() => {
    if (location.state?.activeTab !== undefined) {
      setActiveTab(location.state.activeTab);

      // If a specific request is selected, open its modal
      if (location.state.selectedRequest) {
        setSelectedJob(location.state.selectedRequest);
        setRealTimeModalOpen(true);
      }

      // Clear the navigation state to prevent re-triggering
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  // On /notary/history mount, fetch live/expired request lists once
  // so data is refreshed without requiring manual API trigger.
  useEffect(() => {
    dispatch(
      fetchActiveRealTimeRequestsOnce({
        currentSessionOverride: currentSession,
      }),
    );
    dispatch(
      fetchExpiredExpeditedRequestsOnce({
        currentSessionOverride: currentSession,
      }),
    );
  }, [dispatch, currentSession, currentUser?.notary_admin_id]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await dispatch(fetchNotaryData(currentSession));
      await Promise.all([
        dispatch(fetchActiveRealTimeRequestsOnce({ force: true })),
        dispatch(fetchExpiredExpeditedRequestsOnce({ force: true })),
      ]);
      setSuccess("Data refreshed successfully!");
    } catch (error) {
      console.error("Error refreshing notary data:", error);
      setError("Failed to refresh data. Please try again.");
    } finally {
      setRefreshing(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Modal handlers
  const handleUpcomingNotarizationClick = (job) => {
    setSelectedJob(job);
    setUpcomingModalOpen(true);
  };

  const handlePastNotarizationClick = (job) => {
    setSelectedJob(job);
    setPastModalOpen(true);
  };

  const handleRealTimeRequestClick = (request) => {
    setSelectedJob(request);
    setRealTimeModalOpen(true);
  };

  const handleExpiredExpeditedRequestClick = (request) => {
    setSelectedJob(request);
    setExpiredExpeditedModalOpen(true);
  };

  // const handleDismissRealTimeRequest = async (requestId) => {
  //   try {
  //     await axios.post(
  //       `${process.env.REACT_APP_BASE_URL}/nocApi/notary/dismiss-real-time-request`,
  //       {
  //         requestId,
  //         currentSession,
  //       },
  //       { withCredentials: true },
  //     );

  //     // Remove from Redux state immediately
  //     dispatch({ type: "DISMISS_RON_REQUEST", payload: requestId });
  //   } catch (error) {
  //     // If backend says already gone/not found, remove locally to prevent loops
  //     const status = error?.response?.status;
  //     if (status === 400 || status === 404) {
  //       dispatch({ type: "DISMISS_RON_REQUEST", payload: requestId });
  //     } else {
  //       console.error("Error dismissing request:", error);
  //     }
  //   }
  // };

  const handleRealTimeModalClose = () => {
    setRealTimeModalOpen(false);
    setSelectedJob(null);
  };

  const rejectRequestHandler = () => {
    // if (selectedJob?.id) {
    //   handleDismissRealTimeRequest(selectedJob.id);
    // }
  };

  const buildExpiredExpeditedAcceptUrl = (request) => {
    const notaryId = currentUser?.id;
    const instantToken = request?.instant_token;
    const targetId = request?.cst_packageID || request?.task_id;
    const signeeToken =
      request?.signee_reg_token ||
      request?.reg_token ||
      request?.st ||
      request?.signee_token;
    const signer = request?.signer;
    const expiredRequestId = request?.id;
    const requiresSigneeToken = signer === "owner";

    if (
      !notaryId ||
      !instantToken ||
      !targetId ||
      !signer ||
      (requiresSigneeToken && !signeeToken)
    ) {
      return "";
    }

    const params = new URLSearchParams({
      ni: notaryId,
      it: instantToken,
      ti: targetId,
      reason: signer,
      eid: expiredRequestId || "",
    });

    if (signeeToken) {
      params.set("st", signeeToken);
    }
    params.set("fe", true);

    return `/notary/accept-instant?${params.toString()}`;
  };

  const handleAcceptExpiredExpeditedRequest = (request) => {
    const acceptUrl = buildExpiredExpeditedAcceptUrl(request);
    if (!acceptUrl) {
      setError(
        "Unable to open acceptance flow: missing required request parameters.",
      );
      return;
    }

    const newTab = openInNewTab(acceptUrl);
    if (!newTab) {
      setError(
        "Popup blocked. Please allow popups for this site, then click Accept again.",
      );
      return;
    }

    setSuccess("Opened acceptance flow in a new tab.");
    setExpiredExpeditedModalOpen(false);
    setSelectedJob(null);
  };

  const openExpiredExpeditedAcceptConfirm = (request) => {
    setExpiredAcceptConfirmJob(request);
    setExpiredAcceptConfirmOpen(true);
  };

  const handleConfirmExpiredExpeditedAccept = () => {
    if (!expiredAcceptConfirmJob) return;
    handleAcceptExpiredExpeditedRequest(expiredAcceptConfirmJob);

    const acceptUrl = buildExpiredExpeditedAcceptUrl(expiredAcceptConfirmJob);
    if (acceptUrl) {
      setExpiredAcceptConfirmOpen(false);
      setExpiredAcceptConfirmJob(null);
    }
  };

  const handleRequestAcceptFromExpiredModal = (request) => {
    setExpiredExpeditedModalOpen(false);
    openExpiredExpeditedAcceptConfirm(request);
  };

  const handleAcceptRealTimeRequest = async (requestId) => {
    try {
      // Find the request to get the accept_url
      const request = realTimeRequests.find((req) => req.id === requestId);

      if (!request) {
        setError("Request not found");
        return;
      }

      // Navigate to the accept URL which contains all the necessary parameters
      // This will trigger the actual notarization acceptance flow
      const domainURL =
        process.env.REACT_APP_NODE_ENV === "development"
          ? "http://localhost:3000"
          : process.env.REACT_APP_URL;

      const acceptURL = `${domainURL}/notary/accept-instant?ni=${currentUser?.id}&it=${request?.instant_token}&ti=${request?.task_id}&st=${request?.signee_token}&reason=${request?.signer}`;

      // Remove from Redux state immediately (optimistic update)
      // dispatch({ type: "DISMISS_RON_REQUEST", payload: requestId });

      setSuccess("Opening notarization session...");

      // Close modal
      setRealTimeModalOpen(false);
      setSelectedJob(null);

      // Navigate to acceptance page in a new tab
      openInNewTab(acceptURL);
    } catch (error) {
      setError(error.message || "Failed to accept real-time request");
    }
  };

  const handleCompletedCloseModal = () => {
    setCompletedModalOpen(false);
  };

  const handleDocumentDownload = async (job) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/notary/download-documents`,
        {
          jobId: job.id,
          currentSession,
        },
        {
          withCredentials: true,
          responseType: "blob",
        },
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `notarization_${job.id}_documents.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setSuccess("Documents downloaded successfully!");
    } catch (error) {
      setError("Failed to download documents");
    }
  };

  const getStableNotaryRowId = (row) => {
    const taskIdentifier = row?.id || row?.task_id || row?.cst_packageID || "";
    const signerRole =
      typeof row?.signer === "string"
        ? row.signer
        : row?.notarization?.owner
          ? "owner"
          : row?.notarization?.contractor
            ? "contractor"
            : "unknown";
    const signerEmail = (
      row?.signer_details?.email ||
      row?.signer?.email ||
      row?.notarization?.personal?.email ||
      ""
    )
      .trim()
      .toLowerCase();
    const notarizationId =
      row?.notarization_id || row?.notarization?.notarization?.bn_id || "";
    const timestampHint =
      row?.completed_at || row?.cancelled_at || row?.booked_date || "";

    return [
      taskIdentifier,
      signerRole,
      signerEmail,
      notarizationId,
      timestampHint,
    ].join("|");
  };

  return (
    <>
      {/* Snackbar notifications */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="error" onClose={() => setError("")}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={() => setSuccess("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="success" onClose={() => setSuccess("")}>
          {success}
        </Alert>
      </Snackbar>

      {loading && !hasLoadedOnce ? (
        <Box
          sx={{
            display: "flex",
            width: "100%",
            height: "100vh",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
          }}
        >
          <CircularProgress color="warning" size="10rem" />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading your notarization dashboard...
          </Typography>
        </Box>
      ) : (
        <Container maxWidth="xxl" sx={{ py: 3 }}>
          <Grid container spacing={3}>
            {/* Header Section */}
            <Grid size={{ xs: 12 }}>
              <Card sx={{ backgroundColor: "#1976d2", color: "white" }}>
                <CardContent>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box>
                      <Typography
                        variant={mobileSize ? "h5" : "h4"}
                        fontWeight="bold"
                      >
                        Notarization Management Dashboard
                      </Typography>
                      <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
                        Manage your appointments, view history, and respond to
                        real-time requests
                      </Typography>
                    </Box>
                    <Box display="flex" gap={1}>
                      <Tooltip title="Refresh All Data">
                        <IconButton
                          onClick={handleRefresh}
                          disabled={refreshing}
                          sx={{ color: "white" }}
                        >
                          <Refresh
                            sx={{
                              animation: refreshing
                                ? "spin 1s linear infinite"
                                : "none",
                            }}
                          />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Main Content Tabs */}
            <Grid size={{ xs: 12 }}>
              <Card>
                <Box sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}>
                  <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    variant={mobileSize ? "scrollable" : "standard"}
                    scrollButtons="auto"
                  >
                    <Tab
                      icon={<Assessment />}
                      label="Upcoming Appointments"
                      sx={{ minWidth: mobileSize ? 120 : 160, pt: 2 }}
                    />
                    <Tab
                      icon={<History />}
                      label="Past Notarizations"
                      sx={{ minWidth: mobileSize ? 120 : 160, pt: 2 }}
                    />
                    <Tab
                      icon={
                        <Badge
                          badgeContent={visibleRealTimeRequests.length}
                          color="error"
                        >
                          <Timeline />
                        </Badge>
                      }
                      label="Live Requests"
                      sx={{ minWidth: mobileSize ? 120 : 160, pt: 2 }}
                    />
                    <Tab
                      icon={
                        <Badge
                          badgeContent={expiredExpeditedRequests.length}
                          color="error"
                        >
                          <Schedule />
                        </Badge>
                      }
                      label="Expired Requests"
                      sx={{ minWidth: mobileSize ? 120 : 160, pt: 2 }}
                    />
                  </Tabs>
                </Box>

                <CardContent sx={{ p: 0 }}>
                  {/* Tab Panel 0: Upcoming Notarizations */}
                  {activeTab === 0 && (
                    <Fade in={true}>
                      <Box sx={{ p: 2 }}>
                        <Typography
                          variant="h6"
                          sx={{ mb: 2, display: "flex", alignItems: "center" }}
                        >
                          <Assessment sx={{ mr: 1 }} />
                          Upcoming Notarization Appointments
                        </Typography>
                        <DataGrid
                          sx={{
                            minHeight: 400,
                            ...commonDataGridSx,
                          }}
                          rows={dedupedUpcomingNotarizations}
                          columns={upcomingNotarizationColumns(
                            extraSmall,
                            tabletSize,
                            currentUser,
                            handleUpcomingNotarizationClick,
                          )}
                          getRowId={getStableNotaryRowId}
                          initialState={{
                            pagination: {
                              paginationModel: {
                                pageSize: tabletSize ? 5 : 10,
                              },
                            },
                          }}
                          pageSizeOptions={[5, 10, 25, 50]}
                          disableRowSelectionOnClick
                          rowHeight={extraSmall ? 115 : tabletSize ? 95 : 36}
                          loading={refreshing}
                        />
                      </Box>
                    </Fade>
                  )}

                  {/* Tab Panel 1: Past Notarizations */}
                  {activeTab === 1 && (
                    <Fade in={true}>
                      <Box sx={{ p: 2 }}>
                        <Typography
                          variant="h6"
                          sx={{ mb: 2, display: "flex", alignItems: "center" }}
                        >
                          <History sx={{ mr: 1 }} />
                          Past Notarization History
                        </Typography>
                        <DataGrid
                          sx={{
                            minHeight: 400,
                            "& .MuiDataGrid-row": {
                              cursor: "default !important",
                              "&:hover": {
                                cursor: "default !important",
                              },
                            },
                            "& .MuiDataGrid-cell": {
                              cursor: "default !important",
                              "&:hover": {
                                cursor: "default !important",
                              },
                            },
                            ...commonDataGridSx,
                          }}
                          rows={pastNotarizations}
                          columns={pastNotarizationColumns(
                            extraSmall,
                            tabletSize,
                            currentUser,
                            handlePastNotarizationClick,
                            handleDocumentDownload,
                          )}
                          getRowId={getStableNotaryRowId}
                          initialState={{
                            pagination: {
                              paginationModel: {
                                pageSize: tabletSize ? 5 : 10,
                              },
                            },
                          }}
                          pageSizeOptions={[5, 10, 25, 50]}
                          disableRowSelectionOnClick
                          rowHeight={extraSmall ? 115 : tabletSize ? 90 : 48}
                          loading={refreshing}
                        />
                      </Box>
                    </Fade>
                  )}

                  {/* Tab Panel 2: Real-time Requests */}
                  {activeTab === 2 && (
                    <Fade in={true}>
                      <Box sx={{ p: 2 }}>
                        <Box
                          sx={{
                            mb: 2,
                            display: "flex",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: 1,
                          }}
                        >
                          <Typography
                            color="primary"
                            variant="h6"
                            sx={{
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <Timeline sx={{ mr: 1 }} />
                            Real-Time Notarization Requests
                          </Typography>
                          {visibleRealTimeRequests.length > 0 && (
                            <Chip
                              label={`${visibleRealTimeRequests.length} Active`}
                              color="error"
                              size="small"
                            />
                          )}
                        </Box>
                        <DataGrid
                          sx={{
                            ...commonDataGridSx,
                          }}
                          rows={visibleRealTimeRequests}
                          columns={realTimeRequestColumns(
                            extraSmall,
                            tabletSize,
                            currentUser,
                            currentTime,
                            getRequestExpiryTime,
                            handleRealTimeRequestClick,
                            handleAcceptRealTimeRequest,
                          )}
                          getRowId={(row) => row?.id}
                          initialState={{
                            sorting: {
                              sortModel: [
                                { field: "created_at", sort: "desc" },
                              ],
                            },
                            pagination: {
                              paginationModel: {
                                pageSize: tabletSize ? 5 : 10,
                              },
                            },
                          }}
                          pageSizeOptions={[5, 10, 25, 50]}
                          disableRowSelectionOnClick
                          rowHeight={tabletSize ? 95 : 48}
                          loading={refreshing}
                        />
                      </Box>
                    </Fade>
                  )}

                  {/* Tab Panel 3: Expired Requests */}
                  {activeTab === 3 && (
                    <Fade in={true}>
                      <Box sx={{ p: 2 }}>
                        <Typography
                          variant="h6"
                          color="primary"
                          sx={{ mb: 0, display: "flex", alignItems: "center" }}
                        >
                          <Schedule sx={{ mr: 1 }} />
                          Expired Expedited RON Requests
                        </Typography>
                        <Typography
                          variant="body2"
                          color="primary"
                          sx={{
                            mb: 2,
                            ml: 4,
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          Expedited requests that went unaccepted during the
                          5-minute live window. Available to accept for an
                          additional 5 minutes before permanently expiring.
                        </Typography>

                        <DataGrid
                          sx={{
                            minHeight: 400,
                            ...commonDataGridSx,
                          }}
                          rows={expiredExpeditedRequests}
                          columns={expiredRequestColumns(
                            extraSmall,
                            tabletSize,
                            currentUser,
                            currentTime,
                            handleExpiredExpeditedRequestClick,
                            openExpiredExpeditedAcceptConfirm,
                          )}
                          getRowId={(row) => {
                            const taskIdentifier =
                              row?.cst_packageID || row?.task_id || row?.id;
                            return `${taskIdentifier}_${row?.signer || "unknown"}`;
                          }}
                          initialState={{
                            pagination: {
                              paginationModel: {
                                pageSize: tabletSize ? 5 : 10,
                              },
                            },
                          }}
                          pageSizeOptions={[5, 10, 25, 50]}
                          disableRowSelectionOnClick
                          rowHeight={extraSmall ? 85 : tabletSize ? 80 : 48}
                          loading={refreshing}
                        />
                      </Box>
                    </Fade>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Modals */}
          <ScheduledModal
            open={scheduledModalOpen}
            cancelErrorText={cancelErrorText}
            setCancelErrorText={setCancelErrorText}
            rescheduleErrorText={rescheduleErrorText}
            setRescheduleErrorText={setRescheduleErrorText}
            setScheduledModalOpen={setScheduledModalOpen}
            onClose={() => setScheduledModalOpen(false)}
            selectedJob={selectedJob}
            setSelectedScheduledJob={setSelectedJob}
            jobTakenText={false}
            confirmLoading={false}
            selectedButton={selectedButton}
            setSelectedButton={setSelectedButton}
            cancelLoading={cancelLoading}
            setCancelLoading={setCancelLoading}
            emailLoading={emailLoading}
            setEmailLoading={setEmailLoading}
          />

          <CompletedModal
            currentUser={currentUser}
            open={completedModalOpen}
            cancelErrorText={cancelErrorText}
            setCancelErrorText={setCancelErrorText}
            rescheduleErrorText={rescheduleErrorText}
            setRescheduleErrorText={setRescheduleErrorText}
            setCompletedModalOpen={setCompletedModalOpen}
            onClose={handleCompletedCloseModal}
            selectedJob={selectedJob}
            setSelectedScheduledJob={setSelectedJob}
            jobTakenText={false}
            confirmLoading={false}
            selectedButton={selectedButton}
            setSelectedButton={setSelectedButton}
            cancelLoading={cancelLoading}
            setCancelLoading={setCancelLoading}
          />

          <UpcomingNotarizationModal
            open={upcomingModalOpen}
            onClose={() => setUpcomingModalOpen(false)}
            selectedJob={selectedJob}
            currentUser={currentUser}
            currentSession={currentSession}
          />

          <PastNotarizationModal
            open={pastModalOpen}
            onClose={() => setPastModalOpen(false)}
            selectedJob={selectedJob}
            currentUser={currentUser}
            currentSession={currentSession}
          />

          <RealTimeRequestModal
            open={realTimeModalOpen}
            onClose={handleRealTimeModalClose}
            selectedJob={selectedJob}
            currentUser={currentUser}
            onAccept={handleAcceptRealTimeRequest}
            rejectRequestHandler={rejectRequestHandler}
          />

          <ExpiredExpeditedRequestModal
            open={expiredExpeditedModalOpen}
            onClose={() => setExpiredExpeditedModalOpen(false)}
            selectedJob={selectedJob}
            currentUser={currentUser}
            onAccept={handleRequestAcceptFromExpiredModal}
          />

          <Dialog
            open={expiredAcceptConfirmOpen}
            onClose={() => setExpiredAcceptConfirmOpen(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Confirm Request Acceptance</DialogTitle>
            <DialogContent>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Are you sure you want to accept this expired expedited request?
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setExpiredAcceptConfirmOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={handleConfirmExpiredExpeditedAccept}
              >
                Yes, Accept Request
              </Button>
            </DialogActions>
          </Dialog>
        </Container>
      )}

      <style jsx="true">{`
        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </>
  );
};

export default NotaryJobManagement;
